import React, { useEffect } from 'react';
import { useParams } from 'react-router';
import { Box, Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch } from 'src/redux/store';
import { useDataVault } from 'src/contexts/DataVaultContext';
import {
  fetchCertificationSnapshot,
  selectCertificationSnapshot,
} from 'src/redux/slices/DataVault/dataVaultHistoryCertificationDetailSlice';
import type {
  SnapshotSection,
  SnapshotAnswer,
  SnapshotPage,
  SnapshotGroup,
} from 'src/api/dataVault/historyCertifications';
import { QITAnswerRow } from 'src/components/NewRenterInterface/DataVault/components/QITAnswerRow';
import { FileStrip } from 'src/components/NewRenterInterface/DataVault/History/components/SnapshotShared';
import PageHeader from 'src/components/NewRenterInterface/PageHeader/PageHeader';
import { CertificationSnapshotSectionDetailSkeleton } from './CertificationSnapshotSectionDetailSkeleton';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function flattenAnswers(section: SnapshotSection): SnapshotAnswer[] {
  const answers: SnapshotAnswer[] = [];
  section.pages.forEach((p: SnapshotPage) => {
    p.groups.forEach((g: SnapshotGroup) => {
      answers.push(...g.answers);
    });
  });
  return answers;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CertificationSnapshotSectionDetailPage(): React.ReactElement {
  const dispatch = useDispatch<AppDispatch>();
  const { actingOnMemberId } = useDataVault();
  const { certificationId, sectionId } = useParams<{
    certificationId: string;
    sectionId: string;
  }>();
  const certId = certificationId ? parseInt(certificationId, 10) : NaN;
  const secId = sectionId ? parseInt(sectionId, 10) : NaN;

  const entry = useSelector(
    selectCertificationSnapshot(actingOnMemberId, certId)
  );

  useEffect(() => {
    if (!certId || isNaN(certId)) {
      return;
    }
    if (
      entry.status === 'loading' ||
      entry.status === 'succeeded' ||
      entry.status === 'failed'
    ) {
      return;
    }
    dispatch(
      fetchCertificationSnapshot({ certificationId: certId, actingOnMemberId })
    );
  }, [dispatch, certId, actingOnMemberId, entry.status]);

  const certDetailPath = actingOnMemberId
    ? `/renter/data-vault/members/${actingOnMemberId}/history/certifications/${certId}`
    : `/renter/data-vault/history/certifications/${certId}`;

  // ---------------------------------------------------------------------------
  // Loading / Error states
  // ---------------------------------------------------------------------------

  if (!certId || isNaN(certId) || !secId || isNaN(secId)) {
    return (
      <Box>
        <PageHeader title="Section" backRoute={certDetailPath} />
        <Box sx={{ p: 2 }}>
          <Typography
            role="alert"
            sx={{ color: 'text.secondary', fontSize: 14 }}
          >
            Invalid certification or section.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (entry.status === 'loading' || entry.status === 'idle') {
    return (
      <CertificationSnapshotSectionDetailSkeleton backRoute={certDetailPath} />
    );
  }

  if (entry.status === 'failed' || !entry.data) {
    return (
      <Box>
        <PageHeader title="Section" backRoute={certDetailPath} />
        <Box sx={{ p: 2 }}>
          <Typography
            role="alert"
            sx={{ color: 'text.secondary', fontSize: 14 }}
          >
            {entry.error ?? 'Failed to load certification snapshot.'}{' '}
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                dispatch(
                  fetchCertificationSnapshot({
                    certificationId: certId,
                    actingOnMemberId,
                    forceRefresh: true,
                  })
                )
              }
              sx={{ textTransform: 'none', ml: 1 }}
            >
              Retry
            </Button>
          </Typography>
        </Box>
      </Box>
    );
  }

  // ---------------------------------------------------------------------------
  // Find section
  // ---------------------------------------------------------------------------

  const section = entry.data.sections.find((s) => s.id === secId);

  if (!section) {
    return (
      <Box>
        <PageHeader title="Section" backRoute={certDetailPath} />
        <Box sx={{ p: 2 }}>
          <Typography
            role="alert"
            sx={{ color: 'text.secondary', fontSize: 14 }}
          >
            Section not found.
          </Typography>
        </Box>
      </Box>
    );
  }

  const answers = flattenAnswers(section);

  // ---------------------------------------------------------------------------
  // Content
  // ---------------------------------------------------------------------------

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <PageHeader title={section.name} backRoute={certDetailPath} />

      {/* Answer list */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.paper',
          mx: 2,
          mb: 2,
          borderRadius: '16px',
          boxShadow: '0 1px 8px rgba(0,0,0,0.03)',
          overflow: 'hidden',
        }}
      >
        {answers.length === 0 ? (
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: 13,
              p: 2,
            }}
          >
            No answers recorded for this section.
          </Typography>
        ) : (
          answers.map((answer) => (
            <Box key={answer.id}>
              <QITAnswerRow
                qit={{ id: answer.qit_id, label: answer.question_label }}
                answer={{
                  qitId: answer.qit_id,
                  label: answer.question_label,
                  questionType: answer.display_type,
                  displayValue: answer.display_value,
                  lastUpdatedAt: answer.last_updated,
                  isStale: false,
                  files: answer.files.map((f) => ({
                    id: f.id,
                    name: f.filename,
                  })),
                  answeredAt: answer.last_updated,
                }}
                selectionMode={false}
                isSelected={false}
                // eslint-disable-next-line @typescript-eslint/no-empty-function -- read-only mode, selection disabled
                onToggleSelect={() => {}}
              />
              {answer.files.length > 0 && (
                <Box sx={{ px: 2, pb: 1.5 }}>
                  <FileStrip files={answer.files} />
                </Box>
              )}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}
